<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const routeEditorStore = useRouteEditorStore()
const gameStore = useGameStore()
const route = useRoute()

// Always position at top (patrol selector is hidden when route editor is active)
const positionClass = 'top-0'

const isSubmitting = ref(false)
const nameInput = ref<HTMLInputElement | null>(null)

const onSave = async () => {
  if (!routeEditorStore.canSave) return

  isSubmitting.value = true
  try {
    await routeEditorStore.save()
    gameStore.setTinySuccessToastMessage(t('map.route.saved'))
  } catch (error: any) {
    gameStore.setTinyErrorToastMessage(error.response?.data?.message || t('map.route.saveFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const onCancel = () => {
  routeEditorStore.cancel()
}

const onUndo = () => {
  routeEditorStore.removeLastWaypoint()
}

const onNameChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  routeEditorStore.setRouteName(target.value)
}

// Keyboard shortcuts
const handleKeyDown = (event: KeyboardEvent) => {
  // Only handle if on map page and route editor is active
  if (!route.name?.toString().startsWith('MapGalaxy')) return
  if (!routeEditorStore.initialized) return

  // Don't handle shortcuts if user is typing in the name input
  if (document.activeElement === nameInput.value) {
    if (event.key === 'Escape') {
      nameInput.value?.blur()
    }
    return
  }

  if (event.key === 'Escape') {
    onCancel()
  } else if (event.key === 'Delete' || event.key === 'Backspace') {
    event.preventDefault()
    onUndo()
  } else if (event.key === 'Enter' && routeEditorStore.canSave) {
    onSave()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div
    v-if="routeEditorStore.initialized"
    class="absolute w-full text-center flex justify-center pointer-events-none z-40"
    :class="positionClass"
  >
    <div class="flex items-center gap-4 bg-gradient-to-b from-metal-800 to-metal-950 px-4 py-2 pointer-events-auto rounded-b text-metal-300">
      <!-- Cancel button -->
      <DeleteButton @click="onCancel" class="text-sm">
        {{ t('common.actions.cancel') }}
      </DeleteButton>

      <!-- Route name input -->
      <div class="flex flex-col">
        <div class="flex items-center gap-2">
          <input
            ref="nameInput"
            type="text"
            :value="routeEditorStore.routeName"
            @input="onNameChange"
            class="bg-metal-800 border rounded px-2 py-1 text-sm text-metal-200 w-40 focus:outline-none"
            :class="routeEditorStore.routeNameError ? 'border-red-500' : 'border-metal-600 focus:border-metal-500'"
            :placeholder="t('map.route.namePlaceholder')"
          />
          <span class="text-subtext text-sm">
            {{ t('map.route.waypointsCount', { count: routeEditorStore.waypointCount }, routeEditorStore.waypointCount) }}
          </span>
        </div>
        <span v-if="routeEditorStore.routeNameError" class="text-red-400 text-xs mt-1">
          {{ routeEditorStore.routeNameError }}
        </span>
      </div>

      <!-- Action buttons -->
      <div class="flex items-center gap-2">
        <ActionButton
          v-if="routeEditorStore.waypointCount > 0"
          @click="onUndo"
          class="text-sm"
        >
          {{ t('common.actions.undo') }}
        </ActionButton>
        <ActionButton
          :disabled="!routeEditorStore.canSave || isSubmitting"
          @click="onSave"
          class="text-sm"
          :class="{ 'opacity-50 cursor-not-allowed': !routeEditorStore.canSave }"
        >
          {{ isSubmitting ? t('common.actions.saving') : t('common.actions.save') }}
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
